import { useEffect, useState } from 'react';
import { apiFetch } from '@/lib/api/client';
import { formatCents } from '@/lib/format';

interface Overview {
  windowDays: number;
  scope: { orgId: string; storeCount: number };
  tasks: { open: number; completed: number; skipped: number; completionRatePct: number | null };
  incidents: {
    openTotal: number;
    inProgressTotal: number;
    resolvedTotal: number;
    closedTotal: number;
    bySeverity: { critical: number; high: number; medium: number; low: number };
  };
  cash: {
    submittedCount: number;
    approvedCount: number;
    rejectedCount: number;
    submittedSumCents: number;
    approvedSumCents: number;
  };
  inventory: {
    submittedCount: number;
    approvedCount: number;
    rejectedCount: number;
    submittedSumCents: number;
    approvedSumCents: number;
  };
}

/**
 * Reports overview. Surfaces aggregations from the data we already have today
 * (no Toast/7shifts dependency). Sales + labor land in Phase 5.
 */
export function Reports() {
  const [overview, setOverview] = useState<Overview | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiFetch<{ overview: Overview }>('/reports/overview')
      .then((r) => setOverview(r.overview))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  if (error) {
    return (
      <div className="min-h-screen p-6">
        <p className="text-sm text-destructive">{error}</p>
      </div>
    );
  }
  if (!overview) {
    return (
      <div className="min-h-screen p-6">
        <p className="text-sm text-muted-foreground">Loading…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 max-w-5xl mx-auto">
      <header className="border-b border-border pb-4 mb-6">
        <h1 className="text-2xl font-semibold">Reports</h1>
        <p className="text-sm text-muted-foreground">
          Trailing {overview.windowDays} days · {overview.scope.storeCount} store
          {overview.scope.storeCount === 1 ? '' : 's'} in scope. Sales + labor land in Phase 5.
        </p>
      </header>

      <section className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Stat label="Tasks open" value={overview.tasks.open} />
        <Stat label="Completed" value={overview.tasks.completed} />
        <Stat
          label="Completion rate"
          value={
            overview.tasks.completionRatePct === null ? '—' : `${overview.tasks.completionRatePct}%`
          }
        />
      </section>

      <h2 className="text-sm uppercase tracking-wider text-muted-foreground mb-2">Incidents</h2>
      <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Stat label="Open" value={overview.incidents.openTotal} />
        <Stat label="In progress" value={overview.incidents.inProgressTotal} />
        <Stat label="Resolved" value={overview.incidents.resolvedTotal} />
        <Stat label="Closed" value={overview.incidents.closedTotal} />
      </section>
      <div className="grid grid-cols-4 gap-4 mb-8 text-center">
        <SeverityCell
          label="Critical"
          value={overview.incidents.bySeverity.critical}
          severity="critical"
        />
        <SeverityCell label="High" value={overview.incidents.bySeverity.high} severity="high" />
        <SeverityCell
          label="Medium"
          value={overview.incidents.bySeverity.medium}
          severity="medium"
        />
        <SeverityCell label="Low" value={overview.incidents.bySeverity.low} severity="low" />
      </div>

      <h2 className="text-sm uppercase tracking-wider text-muted-foreground mb-2">
        Cash requests (last 7d)
      </h2>
      <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Stat label="Submitted" value={overview.cash.submittedCount} />
        <Stat label="Approved" value={overview.cash.approvedCount} />
        <Stat label="Rejected" value={overview.cash.rejectedCount} />
        <Stat label="Approved $" value={formatCents(overview.cash.approvedSumCents)} />
      </section>

      <h2 className="text-sm uppercase tracking-wider text-muted-foreground mb-2">
        Inventory requests (last 7d)
      </h2>
      <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Stat label="Submitted" value={overview.inventory.submittedCount} />
        <Stat label="Approved" value={overview.inventory.approvedCount} />
        <Stat label="Rejected" value={overview.inventory.rejectedCount} />
        <Stat label="Approved $" value={formatCents(overview.inventory.approvedSumCents)} />
      </section>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-2xl font-semibold mt-1">{value}</div>
    </div>
  );
}

function SeverityCell({
  label,
  value,
  severity,
}: {
  label: string;
  value: number;
  severity: 'critical' | 'high' | 'medium' | 'low';
}) {
  const tone =
    severity === 'critical'
      ? 'bg-destructive/10 text-destructive'
      : severity === 'high'
        ? 'bg-amber-100 text-amber-700'
        : severity === 'medium'
          ? 'bg-amber-50 text-amber-600'
          : 'bg-muted text-muted-foreground';
  return (
    <div className={`rounded-md py-3 ${tone}`}>
      <div className="text-xs">{label}</div>
      <div className="text-xl font-semibold mt-0.5">{value}</div>
    </div>
  );
}
